import React from "react";
import { Dialog } from "@material-ui/core";
import { Alert, Button, CircularProgress } from "@mui/material";
import usePixModalStyles from "./PixPaymentModal.styles";
import { PixPaymentModalProps } from "./types";
import PixHeader from "./sections/PixHeader";
import PixOrderInfo from "./sections/PixOrderInfo";
import PixQrCodeSection from "./sections/PixQrCodeSection";
import PixValueSection from "./sections/PixValueSection";
import PixConfirmation from "./sections/PixConfirmation";
import PixPaymentDetails from "./sections/PixPaymentDetails";
import PixFooter from "./sections/PixFooter";

const PixPaymentModal: React.FC<PixPaymentModalProps> = ({
  open,
  onClose,
  orderNumber,
  orderStatus,
  customerName,
  productCode,
  productName,
  volume,
  deliveryDate,
  qrCodeValue,
  pixCopiaECola,
  amount,
  receiver = "Atem Distribuidora",
  bank = "Banco do Brasil",
  transactionId,
  issueDate,
  paymentStatus,
  onRefreshQrCode,
  loading = false,
  refreshing = false,
  error = null,
}) => {
  const classes = usePixModalStyles();

  const isPending = paymentStatus === "pending";

  // Fallbacks para dados que podem vir do backend futuramente
  const resolvedTransactionId =
    transactionId ||
    `TX-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`;
  const resolvedIssueDate = issueDate || new Date().toLocaleString("pt-BR");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <PixHeader paymentStatus={paymentStatus} onClose={onClose} />

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress color="primary" size={48} />
        </div>
      ) : (
        <>
          <div className={classes.body}>
            {error && (
              <Alert
                severity="error"
                action={
                  onRefreshQrCode && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={onRefreshQrCode}
                      disabled={refreshing}
                    >
                      Tentar novamente
                    </Button>
                  )
                }
              >
                {error}
              </Alert>
            )}

            <PixOrderInfo
              orderNumber={orderNumber}
              orderStatus={orderStatus}
              customerName={customerName}
              productCode={productCode}
              productName={productName}
              volume={volume}
              deliveryDate={deliveryDate}
              paymentStatus={paymentStatus}
            />

            {isPending ? (
              <div className={classes.qrSection}>
                <PixQrCodeSection
                  qrCodeValue={qrCodeValue}
                  pixCopiaECola={pixCopiaECola}
                />
                <PixValueSection amount={amount} />
              </div>
            ) : (
              <PixConfirmation amount={amount} />
            )}

            <PixPaymentDetails
              receiver={receiver}
              bank={bank}
              transactionId={resolvedTransactionId}
              issueDate={resolvedIssueDate}
              paymentStatus={paymentStatus}
            />
          </div>

          <PixFooter
            paymentStatus={paymentStatus}
            onClose={onClose}
            onRefreshQrCode={isPending ? onRefreshQrCode : undefined}
            refreshing={refreshing}
          />
        </>
      )}
    </Dialog>
  );
};

export default PixPaymentModal;
